import { useEffect, useState } from 'react';
import { gamesService, type Game } from '../../services/games';
import {
    gameLanguagesService,
    type GameLanguage,
} from '../../services/gameLanguages';
import { GameCard } from '../../components/gameCard';
import { GamePopup } from '../../components/gamePopup';
import { Panel } from '../../components/ui/panel';
import { useGameLaunch } from '../../hooks/useGameLaunch';
import { useLauncherLocale } from '../../hooks/useLauncherLocale';
import { GamesFilterToolbar, useGamesFilter } from '../accessLink/components/gamesFilterToolbar';
import { usePreviewPublish } from './PreviewPublishContext';
import { hasUnpublishedChanges } from './utils';

import styles from '../accessLink/AccessLink.module.css';

export function Preview() {
    const locale = useLauncherLocale();
    const { isLoggedIn, setCanPublish } = usePreviewPublish();
    const [games, setGames] = useState<Game[]>([]);
    const [gameLanguagesByGameId, setGameLanguagesByGameId] = useState<
        Record<string, GameLanguage>
    >({});
    const [error, setError] = useState<string | null>(null);
    const { handleLaunch, activeGame, handleClose } = useGameLaunch();
    const { filters, setFilters, clearFilters, filterOptions, filteredGames } =
        useGamesFilter(games);

    useEffect(() => {
        if (!isLoggedIn) {
            setCanPublish(null);
            return;
        }

        let isMounted = true;

        gamesService
            .getPreviewPublishDiff()
            .then((diff) => {
                if (isMounted) {
                    setCanPublish(hasUnpublishedChanges(diff));
                }
            })
            .catch((err) => {
                console.warn(
                    'Failed to load preview publish status',
                    err instanceof Error ? err.message : err
                );
            });

        return () => {
            isMounted = false;
        };
    }, [isLoggedIn, setCanPublish]);

    useEffect(() => {
        let isMounted = true;

        gamesService
            .getDraftGames()
            .then((items) => {
                if (isMounted) {
                    setGames(items);
                }
            })
            .catch((err) => {
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load games');
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        gameLanguagesService
            .getDraftGameLanguagesByLocale(locale)
            .then((languagesByGameId) => {
                if (isMounted) {
                    setGameLanguagesByGameId(languagesByGameId);
                }
            })
            .catch((err) => {
                console.warn(
                    'Failed to load game languages',
                    err instanceof Error ? err.message : err
                );
                if (isMounted) {
                    setGameLanguagesByGameId({});
                }
            });

        return () => {
            isMounted = false;
        };
    }, [locale]);

    return (
        <div className={styles.container}>
            {error ? <p>{error}</p> : null}

            {games.length > 0 ? (
                <GamesFilterToolbar
                    filters={filters}
                    filterOptions={filterOptions}
                    visibleCount={filteredGames.length}
                    onFiltersChange={setFilters}
                    onClearFilters={clearFilters}
                />
            ) : null}

            {games.length > 0 && filteredGames.length === 0 ? (
                <Panel className={styles.noResults} role="status">
                    <p className={styles.noResultsTitle}>No games match</p>
                    <p className={styles.noResultsText}>
                        Try a different search term or clear filters to see all games.
                    </p>
                </Panel>
            ) : null}

            {filteredGames.length > 0 ? (
                <div className={styles.grid}>
                    {filteredGames.map((game) => (
                        <GameCard
                            key={game.id}
                            game={game}
                            gameLanguage={gameLanguagesByGameId[game.id] ?? null}
                            onLaunch={handleLaunch}
                        />
                    ))}
                </div>
            ) : null}

            {activeGame ? (
                <GamePopup
                    url={activeGame.url}
                    name={activeGame.name}
                    format={activeGame.format}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
}
